/**
 * Every call this console makes returns one of these. Nothing throws for an
 * ordinary refusal, because a refusal IS an outcome an operator has to read:
 * «this payout is held pending KYB re-verification» is the answer, not an error.
 *
 * The `kind` is what a screen branches on; the `code` is what a message is
 * chosen by. Both come from the api's own RFC 9457 body (`code`, `detail`,
 * `extra`), which every module renders through the same problem filter.
 */

export type FailureKind =
  /** No session, or the api forgot it. The shell sends the operator to sign in. */
  | "unauthorized"
  /** A live session that may not do this — including a passkey-required refusal. */
  | "forbidden"
  /** The route is not on this api build (a 404 route-miss, or a 501). Say so honestly. */
  | "not_available"
  /** Nothing answered. Different from "answered with a refusal". */
  | "unreachable"
  /** The api answered, and its answer is no. `code` names the rule. */
  | "problem";

export interface Failure {
  readonly ok: false;
  readonly kind: FailureKind;
  readonly status: number;
  /** A stable machine code: `merchant.kyb.blocked`, `auth.session.passkey_required`. */
  readonly code: string;
  readonly detail: string;
  /** Whatever the module attached: `extra.kinds`, `extra.available`, `extra.retryAfterS`. */
  readonly extra: Record<string, unknown>;
  /** Field-level messages from a 400, keyed by the field the api named. */
  readonly fields: Record<string, string>;
}

export type ApiResult<T> = { readonly ok: true; readonly data: T } | Failure;

interface ProblemBody {
  code?: unknown;
  detail?: unknown;
  title?: unknown;
  extra?: unknown;
  errors?: unknown;
}

export function failureFrom(status: number, body: unknown): Failure {
  const problem = (typeof body === "object" && body !== null ? body : {}) as ProblemBody;
  const code = typeof problem.code === "string" ? problem.code : `http.${status}`;
  const detail =
    typeof problem.detail === "string"
      ? problem.detail
      : typeof problem.title === "string"
        ? problem.title
        : "";
  const extra =
    typeof problem.extra === "object" && problem.extra !== null
      ? (problem.extra as Record<string, unknown>)
      : {};

  return {
    ok: false,
    kind: kindOf(status, code),
    status,
    code,
    detail,
    extra,
    fields: fieldsFrom(problem.errors),
  };
}

function kindOf(status: number, code: string): FailureKind {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  // A Nest route-miss carries no module code — `http.404` — while a module's own
  // "this thing does not exist" carries one (`merchant.not_found`). The first
  // means the endpoint is not on this build and the screen must say so; the
  // second is an answer about a row.
  if (status === 404 && code.startsWith("http.")) return "not_available";
  if (status === 501) return "not_available";
  return "problem";
}

export const UNREACHABLE: Failure = {
  ok: false,
  kind: "unreachable",
  status: 0,
  code: "admin.api.unreachable",
  detail: "",
  extra: {},
  fields: {},
};

/**
 * Nest's Zod pipe answers 400 with `errors[]`. The shapes differ slightly
 * between modules, so both the `{path, message}` and the `{field, message}`
 * spellings are read rather than one being declared canonical here.
 */
function fieldsFrom(errors: unknown): Record<string, string> {
  if (!Array.isArray(errors)) return {};
  const out: Record<string, string> = {};
  for (const entry of errors) {
    if (typeof entry !== "object" || entry === null) continue;
    const item = entry as { path?: unknown; field?: unknown; message?: unknown };
    const path = Array.isArray(item.path)
      ? item.path.join(".")
      : typeof item.path === "string"
        ? item.path
        : typeof item.field === "string"
          ? item.field
          : null;
    if (path !== null && typeof item.message === "string" && !(path in out)) out[path] = item.message;
  }
  return out;
}
